import { Link } from 'react-router-dom';
import { LegalLayout, LegalSection } from './LegalLayout';
import { usePageMeta } from '../../shared/hooks/usePageMeta';

const p = 'mb-3.5 text-[17px] leading-[1.65] text-[#c7c7cc] last:mb-0';

const SECTIONS = [
  {
    id: 'who-we-are',
    title: 'Who we are'
  },
  {
    id: 'accounts',
    title: 'Accounts'
  },
  {
    id: 'bookings-quotes-pricing',
    title: 'Bookings, quotes & pricing'
  },
  {
    id: 'payment',
    title: 'Payment'
  },
  {
    id: 'cancellations-changes',
    title: 'Cancellations & changes'
  },
  {
    id: 'your-responsibilities',
    title: 'Your responsibilities'
  },
  {
    id: 'equipment-condition',
    title: 'Equipment condition & service delivery'
  },
  {
    id: 'limitation-of-liability',
    title: 'Limitation of liability'
  },
  {
    id: 'disputes',
    title: 'Disputes'
  },
  {
    id: 'termination',
    title: 'Termination'
  },
  {
    id: 'changes-to-these-terms',
    title: 'Changes to these Terms'
  },
  {
    id: 'contact-us',
    title: 'Contact us'
  }
];

export function TermsPage() {
  usePageMeta('Terms of Service', 'The terms and conditions for using RT Equipment & Haulage services.');
  return (
    <LegalLayout
      title="Terms of Service"
      effective="July 1, 2026"
      sections={SECTIONS}
      other={{ to: '/privacy', label: 'Read our Privacy Policy' }}
      intro={
        <>
          <p className={p}>
              These Terms of Service ("Terms") govern your access to and use of the website,
              booking platform, and related services (together, the "Service") operated by
              RT Equipment &amp; Haulage Company Limited ("RT Equipment &amp; Haulage," "we,"
              "us," or "our"), a heavy equipment transport and haulage company based in
              Spanish Town, St. Catherine, Jamaica. By creating an account or using the Service,
              you agree to be bound by these Terms.
            </p>
        </>
      }
    >
        <LegalSection id="who-we-are" num={1} title="Who we are">
            <p className={p}>
              RT Equipment &amp; Haulage operates its own fleet of equipment and its own drivers.
              We are not a marketplace connecting you to independent third-party equipment owners;
              every booking made through the Service is fulfilled directly by us.
            </p>
        </LegalSection>

        <LegalSection id="accounts" num={2} title="Accounts">
            <p className={p}>
              You must create an account to request a booking. You are responsible for keeping your
              login credentials secure and for all activity under your account. You must provide
              accurate contact information (name, phone number, and email) so we can reach you about
              your booking. Accounts are available to customers on this website, and to drivers and
              admin staff (by invitation) through the RT mobile app; see our{' '}
              <Link to="/#contact" className="text-accent hover:underline">contact details</Link>{' '}
              if you need a staff or driver account.
            </p>
        </LegalSection>

        <LegalSection id="bookings-quotes-pricing" num={3} title="Bookings, quotes &amp; pricing">
            <p className={p}>
              All prices are in Jamaican Dollars (JMD). Submitting a booking request is not a
              confirmed order: our team reviews your request and contacts you directly by phone to
              confirm pricing and scheduling. A booking is only confirmed once we've done so. Pricing
              is based on a base fee, a permit fee, and a per-kilometre transport rate determined by
              equipment weight class; same-day bookings may carry a same-day surcharge. We reserve the
              right to correct obvious pricing errors before a booking is confirmed.
            </p>
        </LegalSection>

        <LegalSection id="payment" num={4} title="Payment">
            <p className={p}>
              Invoices are issued for completed and in-progress work as described in the Service.
              Payment terms will be confirmed with you at the time of booking or invoicing. Overdue
              invoices may result in suspension of future bookings until resolved.
            </p>
        </LegalSection>

        <LegalSection id="cancellations-changes" num={5} title="Cancellations &amp; changes">
            <p className={p}>
              You may cancel a booking that has not yet progressed beyond acceptance directly in the
              Service. Once a driver has been assigned or a job is in progress, cancellations may be
              subject to a fee to cover dispatch and equipment costs already incurred. Contact us as
              early as possible if your schedule changes.
            </p>
        </LegalSection>

        <LegalSection id="your-responsibilities" num={6} title="Your responsibilities">
            <p className={p}>
              You are responsible for providing a safe, accessible site for pickup and delivery, and
              for ensuring the equipment being transported is accurately described and safe to
              handle. You agree not to use the Service for any unlawful purpose, and to provide
              accurate pickup/delivery locations and scheduling information.
            </p>
        </LegalSection>

        <LegalSection id="equipment-condition" num={7} title="Equipment condition &amp; service delivery">
            <p className={p}>
              We inspect our fleet before every job. If you believe equipment delivered to you is
              not in the condition described, or a job was not completed as booked, you may raise a
              dispute directly through the Service, and our team will review it. Estimated arrival
              times and durations are provided in good faith but are not guaranteed, as they can be
              affected by traffic, weather, and site conditions beyond our control.
            </p>
        </LegalSection>

        <LegalSection id="limitation-of-liability" num={8} title="Limitation of liability">
            <p className={p}>
              To the fullest extent permitted by law, RT Equipment &amp; Haulage is not liable for
              indirect, incidental, or consequential damages arising from your use of the Service or
              the equipment we transport for you. Nothing in these Terms limits liability that
              cannot be excluded under Jamaican law, including liability for death or personal injury
              caused by our negligence.
            </p>
        </LegalSection>

        <LegalSection id="disputes" num={9} title="Disputes">
            <p className={p}>
              Most issues can be resolved through the dispute feature available on a booking's detail
              page, which our team reviews within 24 hours. Disputes relating to a completed delivery
              must be raised within 2 days of delivery. These Terms are governed by the laws of
              Jamaica, and any dispute not resolved informally will be subject to the exclusive
              jurisdiction of the courts of Jamaica.
            </p>
        </LegalSection>

        <LegalSection id="termination" num={10} title="Termination">
            <p className={p}>
              We may suspend or terminate your account if you violate these Terms, misuse the
              Service, or if we are required to do so by law. You may stop using the Service and
              request account deletion at any time.
            </p>
        </LegalSection>

        <LegalSection id="changes-to-these-terms" num={11} title="Changes to these Terms">
            <p className={p}>
              We may update these Terms from time to time. If we make material changes, we will
              update the effective date above and, where appropriate, notify you directly. Continued
              use of the Service after changes take effect means you accept the updated Terms.
            </p>
        </LegalSection>

        <LegalSection id="contact-us" num={12} title="Contact us">
            <p className={p}>
              Questions about these Terms can be sent to{' '}
              <a href="mailto:rtequipment.co.ltd@gmail.com" className="text-accent hover:underline">
                rtequipment.co.ltd@gmail.com
              </a>{' '}
              or via our{' '}
              <Link to="/#contact" className="text-accent hover:underline">contact details</Link>. See
              also our{' '}
              <Link to="/privacy" className="text-accent hover:underline">Privacy Policy</Link>.
            </p>
        </LegalSection>
    </LegalLayout>
  );
}
